/**
 * useTour hook
 * Generic tour hook — parameterized by tour type.
 *
 * Manages tour state: active tour, step navigation, API calls.
 *
 * @param tourType - the tour type string (e.g., "home_tour", "ritual_tour")
 * @param options.checkOnMount - if true, auto-checks for active tour on mount (for resume modal)
 *                               if false, only starts via beginTour() (for page overlay)
 */

import { useCallback, useEffect, useState } from "react"
import type { TourData } from "../services/tour.service"
import { getTourStatus, updateTour } from "../services/tour.service"

interface UseTourOptions {
  checkOnMount?: boolean
}

export function useTour(tourType: string, { checkOnMount = false }: UseTourOptions = {}) {
  const [tour, setTour] = useState<TourData | null>(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [showOverlay, setShowOverlay] = useState(false)

  // Check for active tour on mount (for resume check)
  useEffect(() => {
    if (!checkOnMount) return
    getTourStatus(tourType)
      .then(data => {
        if (data.active) {
          setTour(data.active)
          setCurrentStep(data.active.currentScreen)
        }
      })
      .catch(() => {})
  }, [checkOnMount, tourType])

  const beginTour = useCallback(
    async (metadata?: Record<string, unknown>) => {
      const { startTour } = await import("../services/tour.service")
      const data = await startTour(tourType, metadata)
      setTour(data)
      setCurrentStep(data.currentScreen)
      setShowOverlay(true)
      return data
    },
    [tourType],
  )

  const goNext = useCallback(async () => {
    if (!tour) return
    const next = currentStep + 1
    setCurrentStep(next)
    await updateTour(tour.id, { action: "progress", currentScreen: next }).catch(() => {})
  }, [tour, currentStep])

  const goBack = useCallback(async () => {
    if (!tour || currentStep <= 1) return
    const prev = currentStep - 1
    setCurrentStep(prev)
    await updateTour(tour.id, { action: "progress", currentScreen: prev }).catch(() => {})
  }, [tour, currentStep])

  const finishTour = useCallback(async () => {
    if (!tour) return
    await updateTour(tour.id, { action: "complete" }).catch(() => {})
    setTour(null)
    setShowOverlay(false)
  }, [tour])

  const dismissTour = useCallback(async () => {
    if (!tour) return
    await updateTour(tour.id, { action: "skip" }).catch(() => {})
    setTour(null)
  }, [tour])

  const closeModal = useCallback(() => {
    setTour(null)
  }, [])

  const resumeAt = useCallback((step: number) => {
    setCurrentStep(step)
    setShowOverlay(true)
  }, [])

  return {
    tour,
    currentStep,
    showOverlay,
    beginTour,
    goNext,
    goBack,
    finishTour,
    dismissTour,
    closeModal,
    resumeAt,
  }
}
